// Standard 12-hour time formatting for the whole app. Kids/parents should
// never see military (24-hour) time. Where an absolute timezone is required,
// we display in US Eastern Time (ET).

const ET_ZONE = "America/New_York";

// "15:00" | "9:5" -> "3:00 PM". Returns "" for blank/invalid input.
export function format12h(hhmm) {
  if (!hhmm && hhmm !== 0) return "";
  const str = String(hhmm).trim();
  const m = str.match(/^(\d{1,2}):?(\d{2})?$/);
  if (!m) return str;
  let h = parseInt(m[1], 10);
  const min = m[2] != null ? parseInt(m[2], 10) : 0;
  if (Number.isNaN(h) || h < 0 || h > 23 || min < 0 || min > 59) return str;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(min).padStart(2, "0")} ${period}`;
}

// (hour, minute) numbers -> "3:00 PM".
export function format12hHM(hour, minute = 0) {
  if (hour == null || Number.isNaN(Number(hour))) return "";
  return format12h(`${hour}:${String(minute ?? 0).padStart(2, "0")}`);
}

// Any Date | ISO string | ms -> localized 12-hour time in ET, e.g. "3:05 PM ET".
export function formatTimeET(input, { withZone = true } = {}) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  const t = d.toLocaleTimeString("en-US", {
    timeZone: ET_ZONE, hour: "numeric", minute: "2-digit", hour12: true,
  });
  return withZone ? `${t} ET` : t;
}

// Any Date | ISO string | ms -> "Aug 10, 2026, 3:05 PM ET".
export function formatDateTimeET(input, { withZone = true } = {}) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  const s = d.toLocaleString("en-US", {
    timeZone: ET_ZONE, month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
  return withZone ? `${s} ET` : s;
}

// Short relative stamp, e.g. "just now", "2h ago", "3d ago". For quick-glance
// notification lists; tap to expand to the absolute time via formatNotifWhen.
export function relativeTime(input) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 0) return "just now";
  if (s < 45) return "just now";
  if (s < 90) return "1m ago";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 7200) return "1h ago";
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 172800) return "yesterday";
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  if (s < 2592000) return `${Math.floor(s / 604800)}w ago`;
  if (s < 31536000) return `${Math.floor(s / 2592000)}mo ago`;
  return `${Math.floor(s / 31536000)}y ago`;
}

// Notification received stamp in the user's own timezone (falls back to US
// Eastern when the user hasn't set one). Includes the short zone label, e.g.
// "Jun 12, 3:05 PM EDT". `tz` is an IANA zone like "America/Chicago".
export function formatNotifWhen(input, tz) {
  if (!input) return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  let zone = tz || ET_ZONE;
  let s, zoneLabel = "";
  try {
    s = d.toLocaleString("en-US", {
      timeZone: zone, month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" }).formatToParts(d);
    zoneLabel = parts.find((p) => p.type === "timeZoneName")?.value || "";
  } catch {
    // Invalid zone string → fall back to Eastern.
    s = d.toLocaleString("en-US", {
      timeZone: ET_ZONE, month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
    zoneLabel = "ET";
  }
  return zoneLabel ? `${s} ${zoneLabel}` : s;
}